import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { isPast, isSameDay } from 'helpers/dates';
import { loadTasksByMonth } from 'actions/profile';
import { Box, Calendar, Loading, SystemText } from 'e4shared';

import Day from './components/day';

export const TaskCalendar = ({ profileId, filter, search }) => {
    const dispatch = useDispatch();
    const today = new Date();

    const [month, setMonth] = useState(today.getMonth());
    const [year, setYear] = useState(today.getFullYear());
    const [showDay, setShowDay] = useState(false);
    const [tasksState, setTasksState] = useState(null);
    const [date, setDate] = useState(today);

    const tasks = useSelector(state => state.profile.taskList.tasks);
    const loading = useSelector(state => state.profile.loading);

    const moveMonth = (m, y) => {
        setMonth(m);
        setYear(y);
    };

    const content = React.useCallback(
        date => {
            if (loading) return <Loading />;

            const todaysIncomplete = tasks.filter(t => isSameDay(t.due, date) && !t.date_Completed);
            const todaysComplete = tasks.filter(t => isSameDay(t.date_Completed, date) && t.date_Completed);
            const todaysAwaiting = todaysComplete.filter(t => t.requires_Manager && !t.approved);
            const todaysNotAwaiting = todaysComplete.filter(t => !t.requires_Manager || t.approved);

            const past = isPast(date);

            const incomplete =
                todaysIncomplete.length === 0 ? null : (
                    <div
                        className="flex pointer"
                        key="incomplete"
                        onClick={() => {
                            setShowDay(true);
                            setTasksState(tasks);
                            setDate(date);
                        }}
                    >
                        <i className={`fa fa-calendar-check mr5 text-${past ? 'red' : 'orange'}`} />
                        <span className="hidden-xs hidden-sm">{`${todaysIncomplete.length} ${
                            past ? SystemText('Tasks/TaskCalendar/Overdue') : SystemText('Tasks/TaskCalendar/Due')
                        }`}</span>
                    </div>
                );

            const complete =
                todaysNotAwaiting.length === 0 ? null : (
                    <div
                        className="flex pointer"
                        key="notawaiting"
                        onClick={() => {
                            setShowDay(true);
                            setTasksState(tasks);
                            setDate(date);
                        }}
                    >
                        <i className="fa fa-calendar-check mr5 text-green" />
                        <span className="hidden-xs hidden-sm">{`${todaysNotAwaiting.length} ${SystemText(
                            'Tasks/TaskCalendar/Completed'
                        )}`}</span>
                    </div>
                );

            const awaiting =
                todaysAwaiting.length === 0 ? null : (
                    <div
                        className="flex pointer"
                        key="awaiting"
                        onClick={() => {
                            setShowDay(true);
                            setTasksState(tasks);
                            setDate(date);
                        }}
                    >
                        <i className="fa fa-calendar-check mr5 text-orange" />
                        <span className="hidden-xs hidden-sm">{`${todaysAwaiting.length} ${SystemText(
                            'Tasks/TaskCalendar/Unapproved'
                        )}`}</span>
                    </div>
                );

            return [incomplete, awaiting, complete];
        },
        [tasks, loading]
    );

    useEffect(() => {
        dispatch(loadTasksByMonth(profileId, filter, search, month + 1, year));
    }, [dispatch, profileId, filter, search, month, year]);

    if (!tasks) {
        return (
            <span data-testid="taskcalendar-loading-testid">
                <Loading />
            </span>
        );
    }

    return (
        <Box id="taskcalendar-testid">
            <Calendar content={content} moveMonth={moveMonth} />
            <Day
                tasks={tasksState}
                show={showDay}
                date={date}
                close={() => {
                    setShowDay(false);
                    setTasksState(null);
                }}
            />
        </Box>
    );
};

export default withRouter(TaskCalendar);
